// MENU-WIRE-1 — THE MEASURE, APPLIED TO THE TREE.
//
// ─── HOW THIS COMPOSES WITH WHAT IS ALREADY THERE ──────────────────────────
//
// Three layers now subtract from one tree, and section 5 of the brief asks which
// wins where they disagree. Established before anything was wired, from the code
// rather than from intention:
//
//   the shelf   `applyAccountLayer` removes a SECTION the merchant hid, and its
//               children go with it.
//   the role    `filterSectionsByRole` removes a child the role may not see, and
//               ALREADY removes a multi-child section whose children have all
//               gone. Its own comment: "the zero clickable rows hide".
//   the measure this file, removing a child the account has never used.
//
// THEY CANNOT DISAGREE, because all three only ever REMOVE. A section switched
// off takes its children with it; a section whose children have all gone is
// dropped by the rule that already existed. "Which wins" has one answer -
// subtraction wins, in both directions - and that is why the existing hop can
// take a third layer without an ordering argument. `useNavSections` already
// records the same reasoning for the first two: "BOTH LAYERS ONLY SUBTRACT:
// hiding composes in either order".
//
// ─── THE EMPTY ROOM, AND THE ROOM WITH NO DOOR ─────────────────────────────
//
// An empty room is a section on screen with nothing in it. The role layer
// already prevents it and this layer inherits that for free.
//
// The room with no door is worse and was NOT prevented: four sections -
// website, marketing, automations and connectors - had every child measured on
// a table only that section can create rows in. At zero the section vanished
// and there was no route back to create the first one. MENU-WIRE-1 promoted the
// way in to day one in each, and `sectionsWithNoWayBack` fails the build if a
// fifth ever appears.

import { MENU_CATALOGUE } from './menuCatalogue'
import { verdictFor, type MenuActivity } from './menuMeasure'
import type { NavSection } from './navConfig'

/** The child ids this account has never used, so the tree can drop them. */
export function unusedChildIds(activity: MenuActivity): Set<string> {
  const out = new Set<string>()
  for (const item of MENU_CATALOGUE) {
    if (item.parent === null) continue
    if (!verdictFor(item, activity).shown) out.add(item.id)
  }
  return out
}

/**
 * THE TREE WITH THE MEASURE APPLIED.
 *
 * Children the account has never used are removed; a multi-child section left
 * with none goes with them. A section with no children of its own (Home) is
 * never touched here - it has nothing to measure and the role layer already
 * says a childless section stands on its own gate.
 *
 * NOTHING IS REORDERED AND NOTHING IS RENAMED. Subtraction only, so this
 * composes with the other two layers in any order.
 */
export function applyMeasureLayer(
  sections: readonly NavSection[],
  activity: MenuActivity,
): NavSection[] {
  const unused = unusedChildIds(activity)
  const out: NavSection[] = []
  for (const section of sections) {
    if (section.children.length === 0) {
      out.push(section)
      continue
    }
    const children = section.children.filter((c) => !unused.has(c.id))
    // A section whose children have all gone is an empty room. Dropped, exactly
    // as the role layer drops one for the same reason.
    if (children.length === 0) continue
    out.push({ ...section, children })
  }
  return out
}

/**
 * THE RULE THAT KEEPS EVERY SECTION REACHABLE.
 *
 * A section every one of whose children is measured can disappear at zero and
 * never return, because the only way to create the rows its measure counts is
 * through the section that is now gone. Returns the section ids in that state;
 * the guard requires it to be empty.
 */
export function sectionsWithNoWayBack(
  // TAKES THE CATALOGUE. Asserted only against the real one, this says merely
  // that it returns nothing, which a function that always returned nothing
  // satisfies for ever. Mutation caught exactly that here, as it did in
  // MENU-MEASURE-1's detectors.
  catalogue: readonly {
    parent: string | null
    dayOne?: string
    unmeasurable?: string
  }[] = MENU_CATALOGUE,
): string[] {
  const bySection = new Map<string, { total: number; survivesEmpty: number }>()
  for (const item of catalogue) {
    if (item.parent === null) continue
    const cur = bySection.get(item.parent) ?? { total: 0, survivesEmpty: 0 }
    cur.total += 1
    if (item.dayOne || item.unmeasurable) cur.survivesEmpty += 1
    bySection.set(item.parent, cur)
  }
  return [...bySection].filter(([, v]) => v.total > 0 && v.survivesEmpty === 0).map(([id]) => id)
}

/** Every route the catalogue can hide, so the guard can prove none is lost. */
export function routesHiddenBy(sections: readonly NavSection[], activity: MenuActivity): string[] {
  const before = new Set<string>()
  for (const s of sections) for (const c of s.children) before.add(c.to)
  const after = new Set<string>()
  for (const s of applyMeasureLayer(sections, activity)) for (const c of s.children) after.add(c.to)
  return [...before].filter((r) => !after.has(r))
}
